import {
  BlockStack,
  Card,
  FormLayout,
  Select,
  Text,
  SkeletonBodyText,
} from "@medentic/polaris-react";
import { useTranslation } from "react-i18next";
import { useCallback, useMemo } from "react";
import { Event } from "@features/events/types";
import { MultiSelectCombobox } from "@shared/ui";
import useEventMetadata from "@features/events/hooks/useEventMetadata";
import useLanguages from "@shared/hooks/common/useLanguages";

interface EventTypeAudienceCardProps {
  eventData: Partial<Event>;
  onDataChange: (updates: Partial<Event>) => void;
  fieldErrors?: Record<string, string>;
}

export default function EventTypeAudienceCard({
  eventData,
  onDataChange,
  fieldErrors = {},
}: EventTypeAudienceCardProps) {
  const { t } = useTranslation();
  const { 
    formatOptions, 
    expertiseLevelOptions, 
    categoryOptions, 
    isLoading: isLoadingMetadata 
  } = useEventMetadata();
  const { languages, isLoading: isLoadingLanguages } = useLanguages();

  // Event type options with empty placeholder
  const eventTypeOptions = useMemo(() => {
    if (isLoadingMetadata) return [{ label: t("common.selectOption"), value: "" }];
    return [
      { label: t("common.selectOption"), value: "" },
      ...formatOptions,
    ];
  }, [formatOptions, t, isLoadingMetadata]);

  const languageOptions = useMemo(() => {
    return languages.map((language) => ({
      label: language.localLabel,
      value: language.iso,
    }));
  }, [languages]);

  // Handle event type change
  const handleEventTypeChange = useCallback(
    (value: string) => {
      onDataChange({ formatId: value });
    },
    [onDataChange]
  );

  // Handle expertise level change
  const handleExpertiseLevelChange = useCallback(
    (newSelected: string[]) => {
      onDataChange({ expertiseLevelIds: newSelected });
    },
    [onDataChange]
  );

  // Handle category change
  const handleCategoryChange = useCallback(
    (newSelected: string[]) => {
      onDataChange({ categoryIds: newSelected });
    },
    [onDataChange]
  );

  // Handle language change
  const handleLanguageChange = useCallback(
    (newSelected: string[]) => {
      onDataChange({ languageCodes: newSelected });
    },
    [onDataChange]
  );

  return (
    <Card>
      <BlockStack gap="400">
        <Text variant="headingMd" as="h2">
          {t("eventDetails.eventTypeAudience.title")}
        </Text>

        <FormLayout>
          <Select
            label={t("eventDetails.eventTypeAudience.type")}
            options={[...eventTypeOptions]}
            value={eventData.formatId || ""}
            onChange={handleEventTypeChange}
            error={fieldErrors.formatId}
            disabled={isLoadingMetadata}
            requiredIndicator
          />

          <BlockStack gap="100">
            <Text variant="bodyMd" as="p" requiredIndicator>
              {t("eventDetails.eventTypeAudience.expertiseLevel")}
            </Text>
            {isLoadingMetadata ? (
              <SkeletonBodyText lines={1} />
            ) : (
              <MultiSelectCombobox
                label={t("eventDetails.eventTypeAudience.expertiseLevel")}
                options={expertiseLevelOptions}
                selected={
                  isLoadingMetadata || expertiseLevelOptions.length === 0
                    ? []
                    : eventData.expertiseLevelIds || []
                }
                onChange={handleExpertiseLevelChange}
                placeholder={t(
                  "eventDetails.eventTypeAudience.selectExpertiseLevel"
                )}
                maxSelected={2}
                error={fieldErrors.expertiseLevelIds}
                requiredIndicator
              />
            )}
            <Text variant="bodySm" tone="subdued" as="p">
              {t("eventDetails.eventTypeAudience.expertiseLevelHelp")}
            </Text>
          </BlockStack>

          <BlockStack gap="100">
            <Text variant="bodyMd" as="p" requiredIndicator>
              {t("eventDetails.eventTypeAudience.categories")}
            </Text>
            {isLoadingMetadata ? (
              <SkeletonBodyText lines={1} />
            ) : (
              <MultiSelectCombobox
                options={categoryOptions}
                selected={
                  isLoadingMetadata || categoryOptions.length === 0
                    ? []
                    : eventData.categoryIds || []
                }
                onChange={handleCategoryChange}
                placeholder={t("eventDetails.eventTypeAudience.typeCategory")}
                maxSelected={4}
                error={fieldErrors.categoryIds}
              />
            )}
            <Text variant="bodySm" tone="subdued" as="p">
              {t("eventDetails.eventTypeAudience.categoriesHelp")}
            </Text>
          </BlockStack>

          {isLoadingLanguages ? (
            <SkeletonBodyText lines={1} />
          ) : (
            <BlockStack gap="100">
              <Text variant="bodyMd" as="p" requiredIndicator>
                {t("eventDetails.eventTypeAudience.languages")}
              </Text>
              <MultiSelectCombobox
                options={languageOptions}
                selected={eventData.languageCodes || []}
                onChange={handleLanguageChange}
                placeholder={t(
                  "eventDetails.eventTypeAudience.selectLanguages"
                )}
                error={fieldErrors.languageCodes}
              />
            </BlockStack>
          )}
        </FormLayout>
      </BlockStack>
    </Card>
  );
}
